import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { resolveLocaleName } from '../router'

export function useLocalePath() {
  const route = useRoute()
  const { locale } = useI18n()

  /**
   * Resolve a route location for a given route key in the current (or specified) locale.
   *
   * @param {string} routeKey - Base route name (e.g. 'Zone', 'Trail')
   * @param {Object} [params] - Route params (e.g. { slug: 'maramures' })
   * @param {string} [targetLocale] - Override locale ('ro' or 'en'). Defaults to current.
   * @returns {Object} Route location object for use with router-link :to or router.push()
   */
  function localePath(routeKey, params, targetLocale) {
    const loc = targetLocale || locale.value
    const name = resolveLocaleName(routeKey, loc)
    return params ? { name, params } : { name }
  }

  /**
   * Get the route location for the current page in a different locale.
   * Used by the LanguageSwitcher to build the "switch to X" link.
   *
   * @param {string} targetLocale - 'ro' or 'en'
   * @returns {Object} Route location object
   */
  function switchLocalePath(targetLocale) {
    const routeKey = route.meta?.routeKey
    if (!routeKey) {
      // Fallback for routes without routeKey (dashboard, shared auth routes)
      return targetLocale === 'en' ? { path: '/en' } : { path: '/' }
    }
    const name = resolveLocaleName(routeKey, targetLocale)
    return { name, params: route.params, query: route.query }
  }

  return { localePath, switchLocalePath }
}
